import { GlBadge, GlIcon, GlTooltip } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import ProjectListItemActions from './project_list_item_actions.vue?vue3';
import ListItemInactiveBadge from '../resource_lists/list_item_inactive_badge.vue?vue3';
import { PROJECT_VISIBILITY_TYPE, VISIBILITY_TYPE_ICON } from '~/visibility_level/constants';
import { ACCESS_LEVEL_LABELS, ACCESS_LEVEL_NO_ACCESS_INTEGER } from '~/access_level/constants';
import { FEATURABLE_ENABLED } from '~/featurable/constants';
import { __, n__, s__, sprintf } from '~/locale';
import { numberToHumanSize, numberToMetricPrefix } from '~/lib/utils/number_utils';
import { TIMESTAMP_TYPE_CREATED_AT, TIMESTAMP_TYPES } from '~/vue_shared/components/resource_lists/constants';
import CiIcon from '../ci_icon/ci_icon.vue?vue3';
import ListItem from '../resource_lists/list_item.vue?vue3';
import ListItemStat from '../resource_lists/list_item_stat.vue?vue3';
import ListItemDescription from '../resource_lists/list_item_description.vue?vue3';
import CiCatalogBadge from './ci_catalog_badge.vue?vue3';
import TopicBadges from '../topic_badges.vue?vue3';
import { projectStarrersPath, projectForksPath } from '~/lib/utils/path_helpers/project';
import { projectMergeRequestsPath } from '~/lib/utils/path_helpers/merge_requests';
import { projectIssuesPath } from '~/lib/utils/path_helpers/issues';
export default {
    name: 'ProjectsListItem',
    i18n: {
        stars: __('Stars'),
        forks: __('Forks'),
        issues: __('Issues'),
        mergeRequests: __('Merge requests'),
        topics: __('Topics'),
        topicsPopoverTargetText: __('+ %{count} more'),
        moreTopics: __('More topics'),
        project: __('Project')
    },
    components: {
        GlIcon,
        GlBadge,
        GlTooltip,
        ListItem,
        ListItemStat,
        ListItemDescription,
        ProjectListItemActions,
        ListItemInactiveBadge,
        CiIcon,
        TopicBadges,
        CiCatalogBadge
    },
    provide () {
        return {
            triggerDeleteLocation: 'list',
            triggerRestoreLocation: 'list'
        };
    },
    props: {
        project: {
            type: Object,
            required: true
        },
        showProjectIcon: {
            type: Boolean,
            required: false,
            default: false
        },
        listItemClass: {
            type: [
                String,
                Array,
                Object
            ],
            required: false,
            default: ''
        },
        timestampType: {
            type: String,
            required: false,
            default: TIMESTAMP_TYPE_CREATED_AT,
            validator (value) {
                return TIMESTAMP_TYPES.includes(value);
            }
        },
        includeMicrodata: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'click-avatar',
        'click-stat',
        'click-topic',
        'hover-stat',
        'hover-visibility',
        'refetch'
    ],
    computed: {
        microdataAttributes () {
            if (!this.includeMicrodata) return {};
            return {
                itemtype: 'https://schema.org/SoftwareSourceCode',
                itemprop: 'owns',
                itemscope: true,
                avatarAttrs: {
                    itemprop: 'image',
                    labelLinkAttrs: {
                        itemprop: 'name'
                    }
                }
            };
        },
        visibility () {
            return this.project.visibility;
        },
        visibilityIcon () {
            return VISIBILITY_TYPE_ICON[this.visibility];
        },
        visibilityTooltip () {
            return PROJECT_VISIBILITY_TYPE[this.visibility];
        },
        visibilityTooltipTarget () {
            return this.$refs?.visibilityIcon?.$el;
        },
        accessLevel () {
            return this.project.accessLevel?.integerValue;
        },
        accessLevelLabel () {
            return ACCESS_LEVEL_LABELS[this.accessLevel];
        },
        shouldShowAccessLevel () {
            return this.accessLevel !== undefined && this.accessLevel !== ACCESS_LEVEL_NO_ACCESS_INTEGER;
        },
        storageSize () {
            if (!this.hasStatistics) return null;
            if (!this.project.statistics) return s__('StorageSize|Unknown');
            return numberToHumanSize(this.project.statistics.storageSize || 0, 1);
        },
        hasStatistics () {
            return Object.hasOwn(this.project, 'statistics');
        },
        starsHref () {
            return projectStarrersPath(this.project.fullPath, {
                organizationPath: null
            });
        },
        mergeRequestsHref () {
            return projectMergeRequestsPath(this.project.fullPath, {
                organizationPath: null
            });
        },
        forksHref () {
            return projectForksPath(this.project.fullPath, {
                organizationPath: null
            });
        },
        issuesHref () {
            return projectIssuesPath(this.project.fullPath, {
                organizationPath: null
            });
        },
        isMergeRequestsEnabled () {
            return this.project.mergeRequestsAccessLevel?.toLowerCase() === FEATURABLE_ENABLED && this.project.openMergeRequestsCount !== undefined;
        },
        isForkingEnabled () {
            return this.project.forkingAccessLevel?.toLowerCase() === FEATURABLE_ENABLED && this.project.forksCount !== undefined;
        },
        isIssuesEnabled () {
            return this.project.issuesAccessLevel?.toLowerCase() === FEATURABLE_ENABLED && this.project.openIssuesCount !== undefined;
        },
        hasTopics () {
            return Boolean(this.project.topics?.length);
        },
        hasStarData () {
            return Number.isInteger(this.project.starCount);
        },
        starCountWithMetricPrefix () {
            return this.hasStarData ? numberToMetricPrefix(this.project.starCount) : undefined;
        },
        openMergeRequestsCountWithMetricPrefix () {
            if (!this.isMergeRequestsEnabled) {
                return null;
            }
            return numberToMetricPrefix(this.project.openMergeRequestsCount);
        },
        forksCountWithMetricPrefix () {
            if (!this.isForkingEnabled) {
                return null;
            }
            return numberToMetricPrefix(this.project.forksCount);
        },
        openIssuesCountWithMetricPrefix () {
            if (!this.isIssuesEnabled) {
                return null;
            }
            return numberToMetricPrefix(this.project.openIssuesCount);
        },
        hasActions () {
            return this.project.availableActions?.length;
        },
        pipelineStatus () {
            return this.project.pipeline?.detailedStatus;
        },
        dataTestid () {
            return `projects-list-item-${this.project.id}`;
        },
        starA11yText () {
            if (!this.hasStarData) return '';
            return sprintf(n__('%{project} has %{number} star', '%{project} has %{number} stars', this.project.starCount), {
                project: this.project.avatarLabel,
                number: this.starCountWithMetricPrefix
            });
        },
        forkA11yText () {
            if (!this.isForkingEnabled) return '';
            return sprintf(n__('%{project} has %{number} fork', '%{project} has %{number} forks', this.project.forksCount), {
                project: this.project.avatarLabel,
                number: this.forksCountWithMetricPrefix
            });
        },
        mrA11yText () {
            if (!this.isMergeRequestsEnabled) return '';
            return sprintf(n__('%{project} has %{number} open merge request', '%{project} has %{number} open merge requests', this.project.openMergeRequestsCount), {
                project: this.project.avatarLabel,
                number: this.openMergeRequestsCountWithMetricPrefix
            });
        },
        openIssueA11yText () {
            if (!this.isIssuesEnabled) return '';
            return sprintf(n__('%{project} has %{number} open issue', '%{project} has %{number} open issues', this.project.openIssuesCount), {
                project: this.project.avatarLabel,
                number: this.openIssuesCountWithMetricPrefix
            });
        }
    },
    methods: {
        onVisibilityTooltipShown () {
            this.$emit('hover-visibility', this.visibility);
        },
        onTopicClick () {
            this.$emit('click-topic');
        }
    }
};
